import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Plus, Search, User, Mail, Phone, MapPin, Building2, 
  MoreHorizontal, Edit2, Archive, RotateCcw, FileText,
  Loader2, ChevronRight, X, TrendingUp, DollarSign,
  AlertTriangle, Clock, CheckCircle, BarChart3, Bell, Send
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Clients() {
  const { t } = useLanguage();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDocs, setClientDocs] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(true);
  
  // Reminder state
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [reminderInvoice, setReminderInvoice] = useState(null);
  const [reminderTone, setReminderTone] = useState("friendly");
  const [reminderResult, setReminderResult] = useState(null);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    company: "",
    tags: [],
    notes: ""
  });

  useEffect(() => {
    loadClients();
    loadAnalytics();
  }, []);

  useEffect(() => {
    loadClients();
  }, [search, showArchived]);

  const loadClients = async () => {
    setLoading(true);
    try {
      let url = `${API}/clients?include_archived=${showArchived}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url);
      setClients(response.data);
    } catch (error) {
      console.error("Failed to load clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API}/clients/analytics/summary`);
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/documents`);
      setClientDocs(response.data);
      setSelectedClient(response.data.client);
    } catch (error) {
      console.error("Failed to load client details:", error);
    }
  };

  const openAddDialog = () => {
    setEditingClient(null);
    setForm({ name: "", email: "", phone: "", address: "", company: "", tags: [], notes: "" });
    setShowDialog(true);
  };

  const openEditDialog = (client) => {
    setEditingClient(client);
    setForm({
      name: client.name || "",
      email: client.email || "",
      phone: client.phone || "",
      address: client.address || "",
      company: client.company || "",
      tags: client.tags || [],
      notes: client.notes || ""
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    
    setSaving(true);
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, form);
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/clients`, form);
        toast.success("Client created");
      }
      setShowDialog(false);
      loadClients();
      loadAnalytics();
    } catch (error) {
      console.error("Failed to save client:", error);
      toast.error("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (client) => {
    try {
      await axios.delete(`${API}/clients/${client.id}`);
      toast.success("Client archived");
      loadClients();
      loadAnalytics();
      if (selectedClient?.id === client.id) {
        setSelectedClient(null);
        setClientDocs(null);
      }
    } catch (error) {
      console.error("Failed to archive client:", error);
      toast.error("Failed to archive client");
    }
  };

  const handleRestore = async (client) => {
    try {
      await axios.put(`${API}/clients/${client.id}/restore`);
      toast.success("Client restored");
      loadClients();
      loadAnalytics();
    } catch (error) {
      console.error("Failed to restore client:", error);
      toast.error("Failed to restore client");
    }
  };

  // Generate reminder
  const openReminderDialog = (invoice) => {
    setReminderInvoice(invoice);
    setReminderResult(null);
    setReminderTone("friendly");
    setShowReminderDialog(true);
  };

  const generateReminder = async () => {
    if (!reminderInvoice) return;
    
    setReminderLoading(true);
    try {
      const response = await axios.post(`${API}/reminders/generate`, {
        invoice_id: reminderInvoice.id,
        tone: reminderTone,
        channel: "email"
      });
      setReminderResult(response.data);
    } catch (error) {
      console.error("Failed to generate reminder:", error);
      toast.error("Failed to generate reminder");
    } finally {
      setReminderLoading(false);
    }
  };

  const sendReminder = async () => {
    if (!reminderInvoice) return;
    
    setSendingReminder(true);
    try {
      const response = await axios.post(`${API}/reminders/send`, {
        invoice_id: reminderInvoice.id,
        tone: reminderTone,
        channel: "email"
      });
      
      if (response.data.sent) {
        toast.success("Reminder sent successfully!");
      } else {
        toast.info(response.data.reason || "Reminder generated but not sent");
      }
      setShowReminderDialog(false);
    } catch (error) {
      console.error("Failed to send reminder:", error);
      toast.error("Failed to send reminder");
    } finally {
      setSendingReminder(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      invoice: "Invoice",
      receipt: "Receipt",
      quote: "Quote",
      "donation-receipt": "Donation",
      "proof-of-payment": "Payment",
      "expense-claim": "Expense"
    };
    return labels[type] || type;
  };

  // Calculate max for chart scaling
  const maxMonthlyIncome = analytics?.monthly_trend?.length > 0 
    ? Math.max(...analytics.monthly_trend.map(m => m.income), 1)
    : 1;

  return (
    <div className="max-w-7xl mx-auto" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Clients</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {clients.length} {showArchived ? "total" : "active"} clients
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowAnalytics(!showAnalytics)}
            data-testid="toggle-analytics-btn"
          >
            <BarChart3 className="w-4 h-4 mr-1.5" />
            {showAnalytics ? "Hide" : "Show"} Analytics
          </Button>
          <Button onClick={openAddDialog} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Add client
          </Button>
        </div>
      </div>

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <div className="mb-8 space-y-4" data-testid="analytics-section">
          {analyticsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : analytics ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-xs uppercase">Total Invoiced</span>
                  </div>
                  <p className="text-2xl font-bold">{formatCurrency(analytics.summary.total_invoiced)}</p>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs uppercase">Total Paid</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(analytics.summary.total_paid)}</p>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Clock className="w-4 h-4 text-amber-500" />
                    <span className="text-xs uppercase">Outstanding</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(analytics.summary.total_outstanding)}</p>
                </div>
                
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <User className="w-4 h-4" />
                    <span className="text-xs uppercase">With Balance</span>
                  </div>
                  <p className="text-2xl font-bold">{analytics.summary.clients_with_outstanding}</p>
                  <p className="text-xs text-muted-foreground">of {analytics.summary.total_clients} clients</p>
                </div>
              </div>

              {/* Monthly Revenue Chart */}
              {analytics.monthly_trend?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-primary" />
                      <span className="font-medium text-sm">Monthly Revenue</span>
                    </div>
                    <span className="text-xs text-muted-foreground">Last 6 months</span>
                  </div>
                  
                  <div className="flex items-end gap-2 h-32">
                    {analytics.monthly_trend.map((month, idx) => (
                      <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div 
                          className="w-full bg-primary/20 rounded-t transition-all hover:bg-primary/30"
                          style={{ 
                            height: `${Math.max((month.income / maxMonthlyIncome) * 100, 4)}%`,
                            minHeight: '4px'
                          }}
                        >
                          <div 
                            className="w-full bg-primary rounded-t"
                            style={{ height: '100%' }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{month.month}</span>
                        <span className="text-xs font-medium">{formatCurrency(month.income)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Clients by Outstanding */}
              {analytics.top_clients?.filter(c => c.outstanding > 0).length > 0 && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      <span className="font-medium text-sm">Clients with Outstanding Balance</span>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {analytics.top_clients.filter(c => c.outstanding > 0).slice(0, 5).map((client) => (
                      <div 
                        key={client.client_id}
                        className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-md hover:bg-muted/50 cursor-pointer"
                        onClick={() => loadClientDetails(client.client_id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{client.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {client.overdue_count > 0 && (
                                <span className="text-red-500">{client.overdue_count} overdue</span>
                              )}
                              {client.overdue_count > 0 && client.pending_count > 0 && " · "}
                              {client.pending_count > 0 && (
                                <span>{client.pending_count} pending</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-amber-600">
                            {formatCurrency(client.outstanding)}
                          </p>
                          <p className="text-xs text-muted-foreground">outstanding</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone..."
            className="pl-9"
            data-testid="client-search"
          />
        </div>
        
        <button
          onClick={() => setShowArchived(!showArchived)}
          className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
            showArchived 
              ? "bg-primary text-primary-foreground" 
              : "bg-muted/50 text-muted-foreground hover:text-foreground"
          }`}
        >
          {showArchived ? "Show active only" : "Show archived"}
        </button>
      </div>

      {/* Main Content */}
      <div className="flex gap-6">
        {/* Client List */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16">
              <User className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="text-muted-foreground mb-4">
                {search ? "No clients found" : "No clients yet"}
              </p>
              {!search && (
                <Button onClick={openAddDialog} variant="outline">
                  <Plus className="w-4 h-4 mr-1.5" />
                  Add your first client
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-0">
              {clients.map((client, index) => {
                // Get client stats from analytics
                const clientStats = analytics?.all_clients?.find(c => c.client_id === client.id);
                
                return (
                  <div
                    key={client.id}
                    onClick={() => loadClientDetails(client.id)}
                    className={`flex items-center gap-3 py-3 px-2 -mx-2 rounded-md cursor-pointer transition-colors ${
                      selectedClient?.id === client.id 
                        ? "bg-primary/5" 
                        : "hover:bg-muted/30"
                    } ${index !== 0 ? "border-t border-border/50" : ""}`}
                    data-testid={`client-row-${client.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      client.archived ? "bg-muted" : 
                      clientStats?.overdue_count > 0 ? "bg-red-100" :
                      clientStats?.outstanding > 0 ? "bg-amber-100" : "bg-primary/10"
                    }`}>
                      <User className={`w-5 h-5 ${
                        client.archived ? "text-muted-foreground" : 
                        clientStats?.overdue_count > 0 ? "text-red-600" :
                        clientStats?.outstanding > 0 ? "text-amber-600" : "text-primary"
                      }`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${client.archived ? "text-muted-foreground" : ""}`}>
                          {client.name}
                        </p>
                        {client.archived && (
                          <span className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
                            Archived
                          </span>
                        )}
                        {clientStats?.overdue_count > 0 && (
                          <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                            {clientStats.overdue_count} overdue
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {[client.email, client.phone, client.company].filter(Boolean).join(" · ") || "No contact info"}
                      </p>
                    </div>

                    {/* Quick stats */}
                    {clientStats && clientStats.outstanding > 0 && (
                      <div className="text-right mr-2">
                        <p className="text-sm font-semibold text-amber-600">
                          {formatCurrency(clientStats.outstanding)}
                        </p>
                        <p className="text-xs text-muted-foreground">outstanding</p>
                      </div>
                    )}
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(client); }}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        {client.archived ? (
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRestore(client); }}>
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Restore
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleArchive(client); }}
                            className="text-red-600"
                          >
                            <Archive className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Client Detail Panel */}
        {selectedClient && clientDocs && (
          <div className="w-96 flex-shrink-0 border-l border-border pl-6">
            <div className="sticky top-6">
              {/* Client Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h2 className="font-semibold">{selectedClient.name}</h2>
                    {selectedClient.company && (
                      <p className="text-sm text-muted-foreground">{selectedClient.company}</p>
                    )}
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0"
                  onClick={() => { setSelectedClient(null); setClientDocs(null); }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-6">
                {selectedClient.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedClient.email}</span>
                  </div>
                )}
                {selectedClient.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedClient.phone}</span>
                  </div>
                )}
                {selectedClient.address && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedClient.address}</span>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-muted/30 rounded-md p-3">
                  <p className="text-xs text-muted-foreground uppercase">Invoiced</p>
                  <p className="text-lg font-semibold">{formatCurrency(clientDocs.stats.total_invoiced)}</p>
                </div>
                <div className="bg-muted/30 rounded-md p-3">
                  <p className="text-xs text-muted-foreground uppercase">Paid</p>
                  <p className="text-lg font-semibold text-emerald-600">{formatCurrency(clientDocs.stats.total_paid)}</p>
                </div>
                <div className="col-span-2 bg-muted/30 rounded-md p-3">
                  <p className="text-xs text-muted-foreground uppercase">Outstanding</p>
                  <p className={`text-xl font-bold ${clientDocs.stats.outstanding > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                    {formatCurrency(clientDocs.stats.outstanding)}
                  </p>
                </div>
              </div>

              {/* Documents */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                  Documents ({clientDocs.stats.total_documents})
                </p>
                
                {clientDocs.documents.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {clientDocs.documents.map((doc) => {
                      const isOverdue = doc.meta.status !== "paid" && doc.meta.due_date && 
                        new Date(doc.meta.due_date) < new Date();
                      
                      return (
                        <div
                          key={doc.id}
                          className={`flex items-center justify-between p-2 rounded-md ${
                            isOverdue ? "bg-red-50 border border-red-200" : "bg-muted/20"
                          } hover:bg-muted/40 transition-colors`}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            <FileText className={`w-4 h-4 ${isOverdue ? "text-red-500" : "text-muted-foreground"}`} />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">
                                {doc.meta.doc_number || getDocTypeLabel(doc.meta.doc_type)}
                              </p>
                              <p className="text-xs text-muted-foreground">{formatDate(doc.meta.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium">
                              {formatCurrency(doc.totals?.total)}
                            </span>
                            {isOverdue && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-100"
                                onClick={(e) => { e.stopPropagation(); openReminderDialog(doc); }}
                                title="Send reminder"
                              >
                                <Bell className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents yet</p>
                )}
              </div>

              {/* Notes */}
              {selectedClient.notes && (
                <div className="mt-6 pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Notes</p>
                  <p className="text-sm text-muted-foreground">{selectedClient.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Client Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingClient ? "Edit client" : "Add client"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Client name"
                className="mt-1"
                data-testid="client-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@example.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone number"
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-muted-foreground">Company</Label>
              <Input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Company name"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs text-muted-foreground">Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Address"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes about this client..."
                className="mt-1 resize-none"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
              {saving ? "Saving..." : editingClient ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Reminder Dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent className="sm:max-w-lg" data-testid="reminder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Send Payment Reminder
            </DialogTitle>
            <DialogDescription>
              Generate an AI-powered reminder for this overdue invoice
            </DialogDescription>
          </DialogHeader>
          
          {reminderInvoice && (
            <div className="space-y-4">
              {/* Invoice Info */}
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{reminderInvoice.meta?.doc_number}</p>
                    <p className="text-sm text-muted-foreground">
                      {reminderInvoice.recipient?.name || "Unknown client"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-amber-600">
                      {formatCurrency(reminderInvoice.totals?.total)}
                    </p>
                    <p className="text-xs text-red-500">
                      Due: {formatDate(reminderInvoice.meta?.due_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Tone Selection */}
              <div>
                <Label className="text-sm">Reminder Tone</Label>
                <Select value={reminderTone} onValueChange={setReminderTone}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly - Warm and non-aggressive</SelectItem>
                    <SelectItem value="professional">Professional - Formal and direct</SelectItem>
                    <SelectItem value="urgent">Urgent - Emphasizes immediate action</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Generate Button */}
              <Button 
                onClick={generateReminder} 
                disabled={reminderLoading}
                variant="outline"
                className="w-full"
              >
                {reminderLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-2" />
                    Generate Reminder
                  </>
                )}
              </Button>

              {/* Generated Reminder */}
              {reminderResult && (
                <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                  {reminderResult.subject && (
                    <p className="text-sm font-medium">Subject: {reminderResult.subject}</p>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{reminderResult.message}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowReminderDialog(false)}>
              Cancel
            </Button>
            {reminderResult && (
              <Button 
                onClick={sendReminder} 
                disabled={sendingReminder || !reminderInvoice?.recipient?.email}
                data-testid="send-reminder-btn"
              >
                {sendingReminder ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send via Email
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
